import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import {
  Plus, X, Loader2, CalendarDays, ChevronLeft, ChevronRight, ChevronDown,
  Sparkles, Target, ClipboardList, Copy as CopyIcon,
} from "lucide-react";
import { api, fmtErr } from "../lib/api";
import { useAuth } from "../lib/auth";

// ------------- Constants -------------
const OBJETIVOS = [
  { key: "awareness", label: "Awareness" },
  { key: "autoridade", label: "Autoridade" },
  { key: "leads_mql", label: "Leads & MQL" },
  { key: "comunidade", label: "Comunidade" },
  { key: "vendas", label: "Vendas" },
];
const STATUS_CAMPAIGN = [
  { key: "planejamento", label: "Planejamento" },
  { key: "producao", label: "Produção" },
  { key: "ativa", label: "Ativa" },
  { key: "encerrada", label: "Encerrada" },
];
const PIPELINE = [
  { key: "ideia", label: "Ideia", color: "#8d8699" },
  { key: "roteiro", label: "Roteiro", color: "#FF7A5A" },
  { key: "gravacao", label: "Gravação", color: "#FF9B5A" },
  { key: "edicao", label: "Edição", color: "#FF4E9E" },
  { key: "aprovacao", label: "Aprovação", color: "#C05BFF" },
  { key: "agendado", label: "Agendado", color: "#8B5CF6" },
  { key: "publicado", label: "Publicado", color: "#4ade80" },
];
const ETAPAS = [
  { key: "topo", label: "Topo", color: "#FF7A5A" },
  { key: "meio", label: "Meio", color: "#FF4E9E" },
  { key: "fundo", label: "Fundo", color: "#C05BFF" },
];
const FORMATOS = ["Reels", "Carrossel", "Stories", "Estático", "Vídeo", "Live"];
const OBJETIVO_SUGGESTIONS = [
  "Descoberta / Educação", "Educação / Autoridade", "Identificação / Confiança",
  "Educação / Conversão", "Alcance / Engajamento", "Comunidade / Retenção",
];

// ------------- Helpers -------------
const pipeMeta = (k) => PIPELINE.find((p) => p.key === k) || PIPELINE[0];
const etapaMeta = (k) => ETAPAS.find((e) => e.key === k);

function todayIso() { return new Date().toISOString().slice(0, 10); }
function monthKey(d) { return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`; }
function firstOfMonth(mesRef) {
  const [y, m] = mesRef.split("-").map(Number);
  return new Date(y, m - 1, 1);
}
function daysInMonth(mesRef) {
  const [y, m] = mesRef.split("-").map(Number);
  return new Date(y, m, 0).getDate();
}
function fmtDateShort(iso) {
  if (!iso) return "";
  const [, m, d] = iso.split("-");
  return `${d}/${m}`;
}
function fmtMonthLabel(mesRef) {
  if (!mesRef) return "";
  const names = ["Janeiro","Fevereiro","Março","Abril","Maio","Junho","Julho","Agosto","Setembro","Outubro","Novembro","Dezembro"];
  const [y, m] = mesRef.split("-").map(Number);
  return `${names[m - 1]}/${y}`;
}
function fmtErrMsg(err) {
  return fmtErr(err.response?.data?.detail) || err.message || "Erro";
}

// ------------- Sub-components -------------
function CreateCampaignModal({ onClose, onCreate }) {
  const now = new Date();
  const mesRef = monthKey(now);
  const [form, setForm] = useState({
    nome: `${fmtMonthLabel(mesRef)} — `,
    mote: "",
    mes_ref: mesRef,
    data_inicio: `${mesRef}-01`,
    data_fim: `${mesRef}-${String(daysInMonth(mesRef)).padStart(2, "0")}`,
    objetivo_social: "autoridade",
  });
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!form.nome.trim() || !form.mes_ref) { toast.error("Preencha nome e mês"); return; }
    setBusy(true);
    try { await onCreate(form); } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,.6)" }} onClick={onClose} data-testid="create-campaign-modal">
      <div className="card p-6 max-w-lg w-full" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-extrabold text-lg">Nova campanha do mês</h2>
          <button onClick={onClose} className="text-muted hover:text-white"><X size={18} /></button>
        </div>
        <div className="grid gap-3">
          <div>
            <label className="text-xs text-muted font-mono uppercase tracking-widest">Mês de referência</label>
            <input type="month" className="input mt-1.5" value={form.mes_ref} onChange={(e) => {
              const mr = e.target.value;
              setForm({ ...form, mes_ref: mr, nome: `${fmtMonthLabel(mr)} — `, data_inicio: `${mr}-01`, data_fim: `${mr}-${String(daysInMonth(mr)).padStart(2, "0")}` });
            }} data-testid="camp-mes" />
          </div>
          <div>
            <label className="text-xs text-muted font-mono uppercase tracking-widest">Nome da campanha</label>
            <input className="input mt-1.5" value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} placeholder="Ex: Agosto/2026 — Carreira nos EUA" data-testid="camp-nome" />
          </div>
          <div>
            <label className="text-xs text-muted font-mono uppercase tracking-widest">Mote (a ideia central do mês)</label>
            <textarea className="input mt-1.5" rows={3} value={form.mote} onChange={(e) => setForm({ ...form, mote: e.target.value })} placeholder="Construir carreira médica nos EUA deixa de parecer distante quando o processo é entendido." data-testid="camp-mote" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-muted font-mono uppercase tracking-widest">Objetivo</label>
              <select className="input mt-1.5" value={form.objetivo_social} onChange={(e) => setForm({ ...form, objetivo_social: e.target.value })} data-testid="camp-objetivo">
                {OBJETIVOS.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs text-muted font-mono uppercase tracking-widest">Fim</label>
              <input type="date" className="input mt-1.5" value={form.data_fim} onChange={(e) => setForm({ ...form, data_fim: e.target.value })} data-testid="camp-fim" />
            </div>
          </div>
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <button onClick={onClose} className="btn-outline text-sm">Cancelar</button>
          <button onClick={submit} disabled={busy} className="btn-grad text-sm flex items-center gap-2" data-testid="camp-create-submit">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} Criar campanha
          </button>
        </div>
      </div>
    </div>
  );
}

function PostCard({ post, onClick }) {
  const st = pipeMeta(post.status);
  const et = etapaMeta(post.etapa_funil);
  return (
    <button
      onClick={onClick}
      className="w-full text-left card-2 p-2 mb-1 rounded transition hover:border-[var(--accent)] block"
      style={{ borderLeft: `3px solid ${et?.color || st.color}` }}
      data-testid={`post-card-${post.id}`}
    >
      <div className="flex items-center gap-1 mb-1">
        <span className="text-[9px] font-mono uppercase tracking-widest text-muted">{post.formato || "—"}</span>
        {post.canal && <span className="text-[9px] chip" style={{ padding: "0 4px" }}>{post.canal}</span>}
      </div>
      <div className="text-xs font-semibold line-clamp-2">{post.tema || <span className="text-muted italic">sem tema</span>}</div>
      <div className="flex items-center gap-1 mt-1 flex-wrap">
        {et && <span className="chip text-[9px]" style={{ padding: "0 4px", color: et.color, borderColor: et.color }}>{et.label}</span>}
        {post.quem_faz && <span className="chip text-[9px] text-muted" style={{ padding: "0 4px" }} title={post.quem_faz}>{post.quem_faz.split(" ")[0]}</span>}
        <span className="chip text-[9px] ml-auto" style={{ padding: "0 4px", color: st.color, borderColor: st.color }}>{st.label}</span>
      </div>
    </button>
  );
}

function MonthView({ campaign, posts, onPostClick, onCellAdd }) {
  const mes = campaign.mes_ref;
  const first = firstOfMonth(mes);
  const startWeekday = first.getDay(); // 0=Sun
  const days = daysInMonth(mes);
  // Group posts by date
  const byDate = {};
  posts.forEach((p) => { if (p.data) { (byDate[p.data] ||= []).push(p); } });

  const cells = [];
  for (let i = 0; i < startWeekday; i++) cells.push(null);
  for (let d = 1; d <= days; d++) {
    const iso = `${mes}-${String(d).padStart(2, "0")}`;
    cells.push({ iso, d });
  }

  return (
    <div className="card overflow-hidden" data-testid="month-view">
      <div className="grid grid-cols-7 text-[10px] uppercase tracking-widest font-mono text-muted border-b" style={{ borderColor: "var(--border)" }}>
        {["Dom","Seg","Ter","Qua","Qui","Sex","Sáb"].map((d) => (
          <div key={d} className="p-2 text-center">{d}</div>
        ))}
      </div>
      <div className="grid grid-cols-7">
        {cells.map((c, i) => (
          <div key={i} className="border-r border-b p-1 min-h-[110px] max-h-[220px] overflow-y-auto" style={{ borderColor: "var(--border)" }}>
            {c && (
              <>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-[11px] font-bold">{c.d}</span>
                  <button onClick={() => onCellAdd(c.iso)} className="text-muted hover:text-white" title="Novo post neste dia" data-testid={`month-add-${c.iso}`}>
                    <Plus size={11} />
                  </button>
                </div>
                {(byDate[c.iso] || []).map((p) => <PostCard key={p.id} post={p} onClick={() => onPostClick(p)} />)}
              </>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function WeekView({ campaign, posts, onWeekSave, onWeekAdd, onPostClick, onCellAdd }) {
  const weeks = campaign.weeks || [];
  const orderedWeeks = [...weeks].sort((a, b) => a.numero - b.numero);
  return (
    <div className="space-y-4" data-testid="week-view">
      {orderedWeeks.length === 0 && (
        <div className="card p-6 text-center text-sm text-muted">
          Nenhuma semana definida ainda.
          <button onClick={onWeekAdd} className="btn-grad text-sm ml-3 inline-flex items-center gap-1"><Plus size={12} /> Criar semana</button>
        </div>
      )}
      {orderedWeeks.map((w) => (
        <WeekRow key={w.week_id} week={w} posts={posts.filter((p) => p.week_id === w.week_id)} onSave={onWeekSave} onPostClick={onPostClick} onCellAdd={(iso) => onCellAdd(iso, w.week_id)} />
      ))}
      {orderedWeeks.length > 0 && (
        <button onClick={onWeekAdd} className="btn-outline text-sm flex items-center gap-2" data-testid="add-week-btn"><Plus size={13} /> Adicionar semana</button>
      )}
    </div>
  );
}

function WeekRow({ week, posts, onSave, onPostClick, onCellAdd }) {
  const [pilar, setPilar] = useState(week.pilar || "");
  const [direcao, setDirecao] = useState(week.direcao || "");
  const debounce = useRef(null);

  useEffect(() => { setPilar(week.pilar || ""); setDirecao(week.direcao || ""); }, [week.week_id, week.pilar, week.direcao]);

  const persist = (nextPilar, nextDirecao) => {
    if (debounce.current) clearTimeout(debounce.current);
    debounce.current = setTimeout(() => {
      onSave({ ...week, pilar: nextPilar, direcao: nextDirecao });
    }, 700);
  };

  // Compute the 7 dates of the week starting at data_inicio
  const start = new Date(week.data_inicio + "T00:00:00");
  const days = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(start); d.setDate(start.getDate() + i);
    return d.toISOString().slice(0, 10);
  });

  return (
    <div className="card p-4" data-testid={`week-row-${week.week_id}`}>
      <div className="grid gap-3 md:grid-cols-[2fr_3fr] mb-3">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-widest grad-text font-bold mb-1">// semana {week.numero} · {fmtDateShort(week.data_inicio)}</div>
          <input
            className="input text-sm font-bold"
            placeholder="PILAR DA SEMANA (ex: Entender o caminho)"
            value={pilar}
            onChange={(e) => { setPilar(e.target.value); persist(e.target.value, direcao); }}
            data-testid={`week-pilar-${week.week_id}`}
          />
        </div>
        <div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-muted mb-1">// direção narrativa</div>
          <input
            className="input text-sm"
            placeholder="Frase narrativa (ex: Apresentar possibilidades e organizar a jornada.)"
            value={direcao}
            onChange={(e) => { setDirecao(e.target.value); persist(pilar, e.target.value); }}
            data-testid={`week-direcao-${week.week_id}`}
          />
        </div>
      </div>
      <div className="grid grid-cols-7 gap-2">
        {days.map((iso, i) => {
          const label = ["Dom","Seg","Ter","Qua","Qui","Sex","Sáb"][new Date(iso + "T00:00:00").getDay()];
          const dayPosts = posts.filter((p) => p.data === iso);
          return (
            <div key={iso} className="card-2 p-2 min-h-[100px]" data-testid={`weekcell-${iso}`}>
              <div className="flex items-center justify-between mb-1">
                <span className="text-[10px] text-muted font-mono">{label} {fmtDateShort(iso)}</span>
                <button onClick={() => onCellAdd(iso)} className="text-muted hover:text-white" title="Novo post" data-testid={`week-add-${iso}`}>
                  <Plus size={11} />
                </button>
              </div>
              {dayPosts.map((p) => <PostCard key={p.id} post={p} onClick={() => onPostClick(p)} />)}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function PostDrawer({ post, campaign, brand, onClose, onChange, onDelete }) {
  const [local, setLocal] = useState(post);
  const [aiBusy, setAiBusy] = useState(null); // "tema" | "direcionamento" | "objetivo"
  const [aiSuggestion, setAiSuggestion] = useState(null); // {campo, text}
  const debounce = useRef(null);
  const pendingPatch = useRef({});
  const navigate = useNavigate();

  useEffect(() => { setLocal(post); setAiSuggestion(null); pendingPatch.current = {}; }, [post?.id]);

  // Flush pending on unmount / drawer close
  useEffect(() => {
    return () => {
      if (debounce.current) { clearTimeout(debounce.current); debounce.current = null; }
      if (Object.keys(pendingPatch.current).length > 0) {
        onChange(pendingPatch.current);
        pendingPatch.current = {};
      }
    };
  }, []);

  const persist = (patch) => {
    setLocal((prev) => ({ ...prev, ...patch }));
    pendingPatch.current = { ...pendingPatch.current, ...patch };
    if (debounce.current) clearTimeout(debounce.current);
    debounce.current = setTimeout(() => {
      const merged = pendingPatch.current;
      pendingPatch.current = {};
      onChange(merged);
    }, 700);
  };

  const currentWeek = campaign?.weeks?.find((w) => w.week_id === local.week_id);

  const suggest = async (campo) => {
    setAiBusy(campo);
    setAiSuggestion(null);
    try {
      const { data } = await api.post(`/calendar/${brand.brand_id}/post/ai-suggest`, {
        campo, tema: local.tema, formato: local.formato, canal: local.canal,
        quem_faz: local.quem_faz, etapa_funil: local.etapa_funil,
        objetivo: local.objetivo, direcionamento: local.direcionamento,
        campaign_mote: campaign?.mote, week_pilar: currentWeek?.pilar, week_direcao: currentWeek?.direcao,
      });
      setAiSuggestion({ campo, text: data.text });
    } catch (err) { toast.error(fmtErrMsg(err)); }
    finally { setAiBusy(null); }
  };

  const applySuggestion = () => {
    if (!aiSuggestion) return;
    persist({ [aiSuggestion.campo]: aiSuggestion.text });
    setAiSuggestion(null);
    toast.success("Sugestão aplicada");
  };

  const openRoteiro = async () => {
    if (local.roteiro_id) {
      navigate("/conteudo");
      return;
    }
    try {
      const { data } = await api.post(`/calendar/${brand.brand_id}/post/${local.id}/roteiro`);
      persist({ roteiro_id: data.id });
      toast.success("Roteiro criado — abrindo em Conteúdo");
      setTimeout(() => navigate("/conteudo"), 400);
    } catch (err) { toast.error(fmtErrMsg(err)); }
  };

  const personas = brand?.personas || [];
  const canais = brand?.canais || [];

  return (
    <div className="fixed inset-0 z-40 flex" style={{ background: "rgba(0,0,0,.5)" }} onClick={onClose} data-testid="post-drawer">
      <div className="ml-auto w-full max-w-xl h-full overflow-y-auto p-6" style={{ background: "var(--panel)" }} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest grad-text font-bold">// post multidimensional</div>
            <h2 className="text-lg font-extrabold">Editar post</h2>
          </div>
          <button onClick={onClose} className="text-muted hover:text-white"><X size={20} /></button>
        </div>

        <div className="space-y-3">
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="text-[10px] text-muted font-mono uppercase tracking-widest">Data</label>
              <input type="date" className="input mt-1 text-sm" value={local.data || ""} onChange={(e) => persist({ data: e.target.value })} data-testid="drawer-data" />
            </div>
            <div>
              <label className="text-[10px] text-muted font-mono uppercase tracking-widest">Formato</label>
              <select className="input mt-1 text-sm" value={local.formato || ""} onChange={(e) => persist({ formato: e.target.value })} data-testid="drawer-formato">
                <option value="">—</option>
                {FORMATOS.map((f) => <option key={f} value={f}>{f}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[10px] text-muted font-mono uppercase tracking-widest">Canal</label>
              <select className="input mt-1 text-sm" value={local.canal || ""} onChange={(e) => persist({ canal: e.target.value })} data-testid="drawer-canal">
                <option value="">—</option>
                {canais.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          </div>

          <div>
            <label className="text-[10px] text-muted font-mono uppercase tracking-widest flex items-center justify-between">
              <span>Tema</span>
              <button onClick={() => suggest("tema")} disabled={aiBusy === "tema"} className="text-[10px] grad-text hover:underline flex items-center gap-1" data-testid="ai-tema-btn">
                {aiBusy === "tema" ? <Loader2 size={10} className="animate-spin" /> : <Sparkles size={10} />} Sugerir com IA
              </button>
            </label>
            <input className="input mt-1 text-sm" value={local.tema || ""} onChange={(e) => persist({ tema: e.target.value })} placeholder="Título curto e provocativo" data-testid="drawer-tema" />
          </div>

          <div>
            <label className="text-[10px] text-muted font-mono uppercase tracking-widest">Quem faz (persona)</label>
            <select className="input mt-1 text-sm" value={local.quem_faz || ""} onChange={(e) => persist({ quem_faz: e.target.value })} data-testid="drawer-quem-faz">
              <option value="">—</option>
              {personas.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>

          <div>
            <label className="text-[10px] text-muted font-mono uppercase tracking-widest">Etapa do funil</label>
            <div className="mt-1 grid grid-cols-3 gap-2">
              {ETAPAS.map((e) => (
                <button
                  key={e.key}
                  onClick={() => persist({ etapa_funil: e.key })}
                  className={`p-2 rounded border text-xs font-bold transition ${local.etapa_funil === e.key ? "text-white" : "card-2 text-muted"}`}
                  style={{ background: local.etapa_funil === e.key ? e.color : "transparent", borderColor: e.color }}
                  data-testid={`drawer-etapa-${e.key}`}
                >
                  {e.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="text-[10px] text-muted font-mono uppercase tracking-widest flex items-center justify-between">
              <span>Objetivo (duplo)</span>
              <button onClick={() => suggest("objetivo")} disabled={aiBusy === "objetivo"} className="text-[10px] grad-text hover:underline flex items-center gap-1" data-testid="ai-objetivo-btn">
                {aiBusy === "objetivo" ? <Loader2 size={10} className="animate-spin" /> : <Sparkles size={10} />} Sugerir com IA
              </button>
            </label>
            <input className="input mt-1 text-sm" value={local.objetivo || ""} onChange={(e) => persist({ objetivo: e.target.value })} list={`obj-sug-${local.id}`} placeholder="Ex: Descoberta / Educação" data-testid="drawer-objetivo" />
            <datalist id={`obj-sug-${local.id}`}>
              {OBJETIVO_SUGGESTIONS.map((s) => <option key={s} value={s} />)}
            </datalist>
          </div>

          <div>
            <label className="text-[10px] text-muted font-mono uppercase tracking-widest flex items-center justify-between">
              <span>Direcionamento (briefing)</span>
              <button onClick={() => suggest("direcionamento")} disabled={aiBusy === "direcionamento"} className="text-[10px] grad-text hover:underline flex items-center gap-1" data-testid="ai-direcionamento-btn">
                {aiBusy === "direcionamento" ? <Loader2 size={10} className="animate-spin" /> : <Sparkles size={10} />} Sugerir com IA
              </button>
            </label>
            <textarea className="input mt-1 text-sm" rows={5} value={local.direcionamento || ""} onChange={(e) => persist({ direcionamento: e.target.value })} placeholder="O que dizer, ângulo, gancho, referências…" data-testid="drawer-direcionamento" />
          </div>

          {aiSuggestion && (
            <div className="card-2 p-3 border-l-4 border-[var(--accent)]" data-testid="ai-suggestion-box">
              <div className="flex items-center justify-between mb-1">
                <span className="font-mono text-[10px] uppercase tracking-widest grad-text font-bold">// sugestão IA · {aiSuggestion.campo}</span>
                <div className="flex gap-2">
                  <button onClick={applySuggestion} className="btn-grad text-xs px-2 py-1" data-testid="apply-suggestion-btn">Aplicar</button>
                  <button onClick={() => setAiSuggestion(null)} className="text-muted hover:text-white"><X size={12} /></button>
                </div>
              </div>
              <p className="text-sm">{aiSuggestion.text}</p>
            </div>
          )}

          <div>
            <label className="text-[10px] text-muted font-mono uppercase tracking-widest">Status (pipeline)</label>
            <select className="input mt-1 text-sm" value={local.status || "ideia"} onChange={(e) => persist({ status: e.target.value })} data-testid="drawer-status">
              {PIPELINE.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
            </select>
          </div>

          <div className="flex items-center justify-between pt-4 border-t" style={{ borderColor: "var(--border)" }}>
            <button onClick={onDelete} className="text-xs text-[var(--error)] hover:underline" data-testid="drawer-delete">Excluir post</button>
            <button onClick={openRoteiro} className="btn-grad text-sm flex items-center gap-2" data-testid="drawer-roteiro">
              <ClipboardList size={14} /> {local.roteiro_id ? "Ver roteiro" : "Gerar roteiro em Conteúdo →"}
            </button>
          </div>

          {campaign && local.campaign_id !== campaign.campaign_id && (
            <button
              onClick={() => persist({ campaign_id: campaign.campaign_id })}
              className="btn-outline text-xs w-full flex items-center justify-center gap-1"
              data-testid="attach-to-campaign"
            >
              <Target size={12} /> Anexar este post à campanha “{campaign.nome}”
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ------------- Main Page -------------
export default function Calendar() {
  const { activeBrand } = useAuth();
  const [brand, setBrand] = useState(activeBrand);
  const [campaigns, setCampaigns] = useState([]);
  const [activeCampaignId, setActiveCampaignId] = useState(null);
  const [campaign, setCampaign] = useState(null);
  const [orphanPosts, setOrphanPosts] = useState([]); // sem data ou sem campaign
  const [view, setView] = useState("mes"); // "mes" | "semana"
  const [showCreate, setShowCreate] = useState(false);
  const [drawerPost, setDrawerPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => { setBrand(activeBrand); }, [activeBrand]);

  useEffect(() => {
    if (!brand) return;
    (async () => {
      setLoading(true);
      try {
        const [lists, camps, cal] = await Promise.all([
          api.get(`/brands/${brand.brand_id}/lists`),
          api.get(`/campaigns/${brand.brand_id}`),
          api.get(`/calendar/${brand.brand_id}`),
        ]);
        setBrand((b) => ({ ...b, personas: lists.data.personas, canais: lists.data.canais }));
        setCampaigns(camps.data);
        // Orphans: posts without a data OR without a campaign_id
        const allPosts = cal.data?.posts || [];
        setOrphanPosts(allPosts.filter((p) => !p.campaign_id || !p.data));
        if (camps.data.length > 0) {
          setActiveCampaignId(camps.data[0].campaign_id);
        } else {
          setActiveCampaignId(null);
          setCampaign(null);
        }
      } catch (err) { toast.error(fmtErrMsg(err)); }
      finally { setLoading(false); }
    })();
  }, [brand?.brand_id]);

  useEffect(() => {
    if (!brand || !activeCampaignId) return;
    (async () => {
      try {
        const r = await api.get(`/campaigns/${brand.brand_id}/${activeCampaignId}`);
        setCampaign(r.data);
      } catch (err) { toast.error(fmtErrMsg(err)); }
    })();
  }, [activeCampaignId, brand?.brand_id]);

  const refetchCampaign = async () => {
    const [c, cal] = await Promise.all([
      api.get(`/campaigns/${brand.brand_id}/${activeCampaignId}`),
      api.get(`/calendar/${brand.brand_id}`),
    ]);
    setCampaign(c.data);
    const allPosts = cal.data?.posts || [];
    setOrphanPosts(allPosts.filter((p) => !p.campaign_id || !p.data));
  };

  const createCampaign = async (form) => {
    try {
      const { data } = await api.post(`/campaigns/${brand.brand_id}`, form);
      setCampaigns((cs) => [data, ...cs]);
      setActiveCampaignId(data.campaign_id);
      setShowCreate(false);
      toast.success("Campanha criada");
    } catch (err) { toast.error(fmtErrMsg(err)); }
  };

  const updateCampaign = async (patch) => {
    setSaving(true);
    try {
      const { data } = await api.put(`/campaigns/${brand.brand_id}/${activeCampaignId}`, patch);
      setCampaign((c) => ({ ...c, ...data }));
      setCampaigns((cs) => cs.map((c) => c.campaign_id === data.campaign_id ? { ...c, ...data } : c));
    } catch (err) { toast.error(fmtErrMsg(err)); }
    finally { setSaving(false); }
  };

  const saveWeek = async (week) => {
    setSaving(true);
    try {
      await api.put(`/campaigns/${brand.brand_id}/${activeCampaignId}/week`, week);
      await refetchCampaign();
    } catch (err) { toast.error(fmtErrMsg(err)); }
    finally { setSaving(false); }
  };

  const addWeek = async () => {
    const existing = campaign?.weeks || [];
    const numero = existing.length + 1;
    // Start on the Sunday that begins the numero-th week WITHIN the campaign range.
    // Anchor to the Sunday of the campaign's first week, then step forward.
    const campStart = new Date(campaign.data_inicio + "T00:00:00");
    const campEnd = new Date(campaign.data_fim + "T00:00:00");
    const dow = campStart.getDay();
    // First Sunday >= campStart (if campStart is Sunday, use it; otherwise move forward)
    const firstSunday = new Date(campStart);
    if (dow !== 0) firstSunday.setDate(campStart.getDate() + (7 - dow));
    const wStart = new Date(firstSunday);
    wStart.setDate(firstSunday.getDate() + (numero - 1) * 7);
    // Clamp: if past campEnd, use campStart to keep the week visible in the month
    if (wStart > campEnd) wStart.setTime(campStart.getTime());
    const iso = wStart.toISOString().slice(0, 10);
    await saveWeek({ numero, data_inicio: iso, pilar: "", direcao: "" });
  };

  const addPost = async (isoDate, weekId) => {
    try {
      const { data } = await api.post(`/calendar/${brand.brand_id}/post`, {
        campaign_id: activeCampaignId,
        week_id: weekId || null,
        data: isoDate,
        formato: "Reels",
        status: "ideia",
      });
      await refetchCampaign();
      setDrawerPost(data);
    } catch (err) { toast.error(fmtErrMsg(err)); }
  };

  const savePost = async (patch) => {
    if (!drawerPost) return;
    setSaving(true);
    try {
      const { data } = await api.put(`/calendar/${brand.brand_id}/post/${drawerPost.id}`, patch);
      setDrawerPost(data);
      await refetchCampaign();
    } catch (err) { toast.error(fmtErrMsg(err)); }
    finally { setSaving(false); }
  };

  const deletePost = async () => {
    if (!drawerPost || !window.confirm("Excluir este post?")) return;
    try {
      await api.delete(`/calendar/${brand.brand_id}/post/${drawerPost.id}`);
      setDrawerPost(null);
      await refetchCampaign();
      toast.success("Post excluído");
    } catch (err) { toast.error(fmtErrMsg(err)); }
  };

  const deleteCampaign = async () => {
    if (!campaign || !window.confirm(`Excluir a campanha "${campaign.nome}"? Os posts serão desvinculados (não apagados).`)) return;
    try {
      await api.delete(`/campaigns/${brand.brand_id}/${campaign.campaign_id}`);
      const rest = campaigns.filter((c) => c.campaign_id !== campaign.campaign_id);
      setCampaigns(rest);
      setActiveCampaignId(rest[0]?.campaign_id || null);
      if (rest.length === 0) setCampaign(null);
      toast.success("Campanha excluída");
    } catch (err) { toast.error(fmtErrMsg(err)); }
  };

  if (!brand) return <div className="p-10 text-muted">Sem marca ativa.</div>;
  if (loading) return <div className="p-10 text-muted font-mono text-xs">carregando…</div>;

  return (
    <div className="p-6 lg:p-10" data-testid="campaigns-page">
      <div className="flex items-start justify-between mb-6 gap-3 flex-wrap">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-widest grad-text font-bold mb-1">// módulo · campanhas & calendário</div>
          <h1 className="text-3xl font-extrabold">Campanhas</h1>
          <p className="text-muted text-sm mt-1 max-w-2xl">A unidade de trabalho é a <strong>campanha do mês</strong>: um mote, semanas com pilar+direção e posts multidimensionais. Ligado ao Conteúdo.</p>
        </div>
        <div className="flex items-center gap-2">
          {saving && <span className="text-xs text-muted flex items-center gap-1"><Loader2 size={12} className="animate-spin" /> salvando…</span>}
          <button onClick={() => setShowCreate(true)} className="btn-grad text-sm flex items-center gap-2" data-testid="new-campaign-btn"><Plus size={14} /> Nova campanha</button>
        </div>
      </div>

      {/* Campaign selector + card */}
      {campaigns.length === 0 && orphanPosts.length === 0 && (
        <div className="card p-10 text-center" data-testid="no-campaigns-cta">
          <div className="inline-flex w-14 h-14 rounded-full grad-bg items-center justify-center mb-4"><CalendarDays size={26} className="text-white" /></div>
          <h3 className="text-xl font-extrabold mb-2">Nenhuma campanha ainda</h3>
          <p className="text-sm text-muted mb-5 max-w-md mx-auto">Comece criando a campanha do mês. Cada campanha ancora um mote, semanas com pilar+direção, e todos os posts do período.</p>
          <button onClick={() => setShowCreate(true)} className="btn-grad text-sm flex items-center gap-2 mx-auto"><Plus size={14} /> Criar campanha do mês</button>
        </div>
      )}

      {campaign && (
        <>
          <div className="card p-5 mb-5" data-testid="campaign-card">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-3 flex-wrap">
                <div className="relative">
                  <select
                    value={activeCampaignId}
                    onChange={(e) => setActiveCampaignId(e.target.value)}
                    className="input text-lg font-extrabold pr-8"
                    data-testid="campaign-select"
                  >
                    {campaigns.map((c) => <option key={c.campaign_id} value={c.campaign_id}>{c.nome}</option>)}
                  </select>
                  <ChevronDown size={14} className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none text-muted" />
                </div>
                <select
                  value={campaign.status}
                  onChange={(e) => updateCampaign({ status: e.target.value })}
                  className="input py-1 text-xs w-auto"
                  style={{ maxWidth: 160 }}
                  data-testid="campaign-status"
                >
                  {STATUS_CAMPAIGN.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
                </select>
                <select
                  value={campaign.objetivo_social || ""}
                  onChange={(e) => updateCampaign({ objetivo_social: e.target.value })}
                  className="input py-1 text-xs w-auto"
                  style={{ maxWidth: 180 }}
                  data-testid="campaign-objetivo"
                >
                  {OBJETIVOS.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
                </select>
              </div>
              <div className="flex items-center gap-2">
                <div className="chip text-xs">{campaign.stats?.publicados || 0} / {campaign.stats?.total || 0} publicados</div>
                <button onClick={deleteCampaign} title="Excluir campanha" aria-label="Excluir campanha" className="text-muted hover:text-[var(--error)] text-xs p-1" data-testid="delete-campaign-btn"><X size={16} /></button>
              </div>
            </div>
            <div className="mt-3">
              <div className="font-mono text-[10px] uppercase tracking-widest text-muted mb-1">// mote</div>
              <textarea
                className="input text-sm"
                rows={2}
                value={campaign.mote || ""}
                onChange={(e) => setCampaign((c) => ({ ...c, mote: e.target.value }))}
                onBlur={() => updateCampaign({ mote: campaign.mote })}
                placeholder="A ideia central do mês em uma frase"
                data-testid="campaign-mote"
              />
            </div>
            <div className="mt-3 h-1.5 rounded" style={{ background: "var(--panel-2)" }}>
              <div className="h-full rounded grad-bg" style={{ width: `${campaign.stats?.total ? (campaign.stats.publicados / campaign.stats.total) * 100 : 0}%` }} />
            </div>
          </div>

          {/* View toggle */}
          <div className="flex items-center gap-1 mb-5 card-2 p-1 inline-flex" data-testid="view-toggle">
            {["mes","semana"].map((v) => (
              <button key={v} onClick={() => setView(v)} className={`px-3 py-1.5 rounded text-xs ${view === v ? "grad-bg text-white" : "text-muted"}`} data-testid={`view-${v}`}>
                {v === "mes" ? "Mês" : "Semana"}
              </button>
            ))}
          </div>

          {view === "mes" ? (
            <MonthView campaign={campaign} posts={campaign.posts || []} onPostClick={setDrawerPost} onCellAdd={(iso) => addPost(iso, null)} />
          ) : (
            <WeekView campaign={campaign} posts={campaign.posts || []} onWeekSave={saveWeek} onWeekAdd={addWeek} onPostClick={setDrawerPost} onCellAdd={addPost} />
          )}
        </>
      )}

      {/* Backlog: posts sem data ou sem campanha */}
      {orphanPosts.length > 0 && (
        <div className="card p-5 mt-6" data-testid="orphan-posts">
          <div className="flex items-center gap-2 mb-2">
            <ClipboardList size={16} className="grad-text" />
            <div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-muted">// backlog</div>
              <h3 className="font-bold text-base">Posts sem data ou sem campanha ({orphanPosts.length})</h3>
            </div>
          </div>
          <p className="text-xs text-muted mb-3">Estes posts existiam antes das campanhas terem sido introduzidas, ou foram criados sem data. Clique para editar e ligá-los à campanha atual.</p>
          <div className="grid gap-2 md:grid-cols-2 lg:grid-cols-3">
            {orphanPosts.map((p) => (
              <div key={p.id} className="card-2 p-2" data-testid={`orphan-post-${p.id}`}>
                <PostCard post={p} onClick={() => setDrawerPost(p)} />
              </div>
            ))}
          </div>
        </div>
      )}

      {showCreate && <CreateCampaignModal onClose={() => setShowCreate(false)} onCreate={createCampaign} />}
      {drawerPost && (
        <PostDrawer
          post={drawerPost}
          campaign={campaign}
          brand={brand}
          onClose={() => setDrawerPost(null)}
          onChange={savePost}
          onDelete={deletePost}
        />
      )}
    </div>
  );
}
